<template>
  <div data-component-group="business-form" data-component="notifications">
    <slot name="beforeForm" />

    <div v-if="!isDisabled" class="component-header">
      <magic-container>
        <v-autocomplete
          v-model="country"
          :items="threadSubmissions"
          prepend-inner-icon="public"
          label="Country"
          item-title="name"
          item-value="code"
          :return-object="true"
          required
          :readonly="isDisabled"
          :error-messages="countryErrors"
          @blur="v$.country.$touch()"
        />
        <v-autocomplete
            v-model="notification"
            :items="notificationTypes"
            prepend-inner-icon="label"
            label="Notification type"
            item-title="name"
            item-value="id"
            :return-object="true"
            required
            :readonly="isDisabled"
            :error-messages="notificationErrors"
            @blur="v$.notification.$touch()"
            @focus="onFocusNotification"
          />
      </magic-container>

      <div class="component-actions">
        <v-btn
          color="secondary"
          :loading="isLoading"
          @click="onUpdate">
          <v-icon>edit</v-icon>
          {{ triggerLabel }}
        </v-btn>
      </div>
    </div>
    <div class="component-body">
      <Removables
        v-if="hasItems"
        :items="notifications"
        :is-disabled="isDisabled"
        :on-remove="onRemove"
      >
        <template v-slot:item="scope" >
          <magic-container class="align-center">
            <div class="d-flex align-center" data-cols="5">
              <v-icon> public </v-icon>
              {{ scope.item.country_notify.name }}
            </div>
            <AmountLabel
              label="Type"
              size="lg"
              :value="scope.item.notify_when.name"
            />
          </magic-container>
        </template>
      </Removables>
    </div>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators';
  import Removables from '../../lists/Removables';
  import AmountLabel from '../../labels/Amount';
  import { isStr } from '../../../utils';
  import { find } from 'lodash-es';

  // TODO: Translate component

  export default {
    components: {
      Removables,
      AmountLabel,
    },
    setup () {
    return {
      v$: useVuelidate()
    }
  },
    validations() {
      return {
        country: {
          required,
        },
        notification: {
          required,
        },
      };
    },
    props: {
      business: {
        type: Object,
        default: undefined,
      },
      triggerLabel: {
        type: String,
        default: 'Enable',
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
      onSubmit: {
        type: Function,
        default: () => {},
      },
    },
    data() {
      return {
        country: null,
        notification: null,
        isLoading: false,
      };
    },
    computed: {
      userCountries() {
        return this.$store.getters['users/countries'];
      },
      threadSubmissions() {
        const { threadSubmissions } = this.$store.getters['businesses/activeItem'] || {}  ;

        return threadSubmissions?.filter((item) => {
          return find(this.userCountries, { code: item.code });
        });
      },
      notificationTypes() {
        return this.$store.getters['businesses/notificationTypes'];
      },
      notifications() {
        const { notifications } = this.$store.getters['businesses/activeItem'] || {};
        return notifications || [];
      },
      hasItems() {
        return this.notifications.length > 0;
      },
      notificationErrors() {
        return this.v$.notification.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Notification type is required';
          }
          return error.$message;
        });
      },
      countryErrors() {
        return this.v$.country.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Country is required';
          }
          return error.$message;
        });
      },
    },
    watch: {
      'business.id': {
        immediate: true,
        handler(nextId, prevId) {
          if (nextId) {
            if (prevId !== undefined) {
              this.clear();
            }
            this.getNotifications(this.business);
          }
        }
      },
    },
    methods: {
      getNotificationTypes() {
        return this.$store.dispatch('businesses/getNotificationTypes');
      },
      onUpdate() {
        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }
        this.isLoading = true;
        const { country, notification, onSubmit } = this;

        onSubmit({
          country,
          notification,
        }).then((res)=>{
          this.clear();
          return res;
        }).catch(err=>err)
        .finally(()=>{
          this.isLoading = false;
        });
      },
      canSubmitRequest() {
        const { country, notification } = this;

        return isStr(country?.code) && isStr(notification?.code);
      },
      getNotifications({ id }) {
        const query = { id };
        this.$store.dispatch('users/getNotificationAccountTypes');
        return this.$store.dispatch('businesses/getNotifications', query);
      },
      onRemove(item) {
        const query = {
          id: this.business.id,
          notification_id: item.id,
        };

        return this.$store.dispatch('businesses/removeNotification', query);
      },
      clear() {
        this.v$.$reset();
        this.country = null;
        this.notification = null;
      },
      onFocusNotification() {
        if (this.notificationTypes.length === 0) {
          this.getNotificationTypes();
        }
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='business-form'][data-component='notifications'] {
  padding: $spacing-xl;
  background-color: map.get($colors, shades, 'white');
  border-top: 1px solid map.get($colors, grey, lighten-2);

  .component-actions {
    text-align: right;

    .v-btn {
      margin: 0;

      .v-icon {
        margin: 0 6px 0 0;
      }
    }
  }

  .labels {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
  }

  .label {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-size: $text-size-sm;
    padding: 2px 6px;
    margin: 0 10px 10px 0;
    border-radius: $border-radius;
    color: map.get($colors, shades, 'white');
    background-color: map.get($colors, primary);

    .v-icon {
      font-size: $text-size-md;
      margin: 0 6px 0 0;
      color: map.get($colors, shades, 'white');
    }
  }

  [data-component='amount'] {
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }

  [data-component='removables'] {
    padding: 16px 0 0 0;

    .v-list {
      padding: 0;
    }

    .v-list-item {
      height: 62px;
      border-radius: $border-radius;
      background-color: map.get($colors, grey, lighten-3);
    }
  }
}
</style>
